import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { PrivateRoute } from './components'
import { ThemeProvider, Theme, responsiveFontSizes } from '@mui/material'
import { WLtheme, WStheme, EdumiamTheme } from './utils/theme'
import CssBaseline from '@mui/material/CssBaseline'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import 'dayjs/locale/fr'
import {
  Login,
  Admin,
  Registration,
  ProviderDashboard,
  ProviderRegistration,
  Task,
  UpdatePaymentMethod,
  RegistrationGroupDashboard,
  Marketplace,
  MarketplaceColdStart,
} from './pages'
import { ApprenticeshipRoutes, EnterpriseRoutes, IperiaRoutes, RegistrationRoutes } from './routes'
import {
  COLD_START_MARKETPLACE_PATHS,
  GROUP_DASHBOARD_PATH,
  GROUP_MARKETPLACE_PATH,
  REGISTRATION_ROUTES_PATH,
} from './routes/paths'
import ChannelDisabled from './pages/iperia/ChannelDisabled'
import ParentValidation from './pages/iperia/ParentValidation'
import DocusignSuccess from './pages/iperia/DocusignSuccess'
import EdumiamLayout from './components/iperia/EdumiamLayout'
import { buildParentPortalSteps } from './utils/iperiaSteps'
import Hotjar from '@hotjar/browser'
import ValidatePaymentMethod from './pages/ValidatePaymentMethod'
import { BrandContext, getBrandConfig } from './utils/brand'
import config from './utils/config'
import {
  isEdumiamHostname,
  isIperiaBiChannelEnabled,
  isIperiaRoute,
  resolveAppTheme,
} from './utils/featureFlags'

function getThemeFromKey(themeKey: ReturnType<typeof resolveAppTheme>): Theme {
  switch (themeKey) {
    case 'edumiam':
      return EdumiamTheme
    case 'sante':
      return WStheme
    default:
      return WLtheme
  }
}

function AppContent() {
  const routeLocation = useLocation()
  const [globalId, setGlobalId] = useState<string | undefined>(undefined)

  const themeKey = resolveAppTheme(
    routeLocation.pathname,
    isEdumiamHostname(),
    isIperiaBiChannelEnabled(),
  )

  const brand = useMemo(
    () =>
      getBrandConfig(
        isIperiaBiChannelEnabled() && (isEdumiamHostname() || isIperiaRoute(routeLocation.pathname))
          ? window.location.hostname
          : undefined,
      ),
    [routeLocation.pathname],
  )

  const selectedTheme = useMemo(() => getThemeFromKey(themeKey), [themeKey])

  useEffect(() => {
    const link: HTMLAnchorElement | null = window.document.querySelector("link[rel~='icon']")
    if (themeKey === 'edumiam') {
      document.title = brand.pageTitle
      if (link) {
        link.href = brand.favicon
      }
    } else if (themeKey === 'sante') {
      document.title = config.APP_NAME
      if (link) {
        link.href = '/WSFavicon.svg'
      }
    } else {
      document.title = config.APP_NAME
      if (link) {
        link.href = '/WLFavicon.svg'
      }
    }
  }, [themeKey, brand.favicon, brand.pageTitle])

  useEffect(() => {
    if (globalId && !isEdumiamHostname()) {
      Hotjar.identify(globalId, {
        global_id: globalId,
      })
    }
  }, [globalId])

  useEffect(() => {
    let previousUrl = ''
    const observer = new MutationObserver(function () {
      if (window.location.href !== previousUrl) {
        previousUrl = window.location.href
        const url = new URL(window.location.href)
        const global_id = url.searchParams.get('global_id')
        if (!globalId && global_id) {
          setGlobalId(global_id)
        }
      }
    })
    const config = { subtree: true, childList: true }
    observer.observe(document, config)
    return () => observer.disconnect()
  }, [globalId])

  return (
    <BrandContext.Provider value={brand}>
      <ThemeProvider theme={responsiveFontSizes(selectedTheme)}>
        <CssBaseline />
        <Routes>
          <Route 
            path="/iperia/:global_id/docusign/success" 
            element={isIperiaBiChannelEnabled() && isEdumiamHostname() ? <DocusignSuccess /> : <ChannelDisabled />} 
          />
          <Route
            path="/iperia/parent"
            element={
              isIperiaBiChannelEnabled() && isEdumiamHostname() ? (
                <EdumiamLayout steps={buildParentPortalSteps()}>
                  <ParentValidation />
                </EdumiamLayout>
              ) : (
                <ChannelDisabled />
              )
            }
          />
          <Route
            path="/iperia/:global_id/*"
            element={isIperiaBiChannelEnabled() && isEdumiamHostname() ? <IperiaRoutes /> : <ChannelDisabled />}
          />
          <Route path="/alternance/*" element={<ApprenticeshipRoutes />} />
          {/* Déclarées avant le splat par lisibilité seulement : React Router classe par
              spécificité du motif, pas par ordre de déclaration (cf. marketplaceRouting.spec.ts). */}
          {COLD_START_MARKETPLACE_PATHS.map((path) => (
            <Route key={path} path={path} element={<MarketplaceColdStart />} />
          ))}
          <Route path={REGISTRATION_ROUTES_PATH} element={<RegistrationRoutes />} />
          <Route path="/entreprise/*" element={<EnterpriseRoutes />} />
          <Route path="/login" element={<Login />} />
          <Route
            path="/admin"
            element={
              <PrivateRoute>
                <Admin />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/registration/:id"
            element={
              <PrivateRoute>
                <Registration />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/sf/task/:task_id"
            element={
              <PrivateRoute>
                <Task />
              </PrivateRoute>
            }
          />
          <Route path="/providers/:provider_id/" element={<ProviderDashboard />} />
          <Route
            path="/providers/:provider_id/registration/:registration_id/"
            element={<ProviderRegistration />}
          />
          <Route path={GROUP_DASHBOARD_PATH} element={<RegistrationGroupDashboard />} />
          <Route path={GROUP_MARKETPLACE_PATH} element={<Marketplace />} />
          <Route path="/mon-echeancier" element={<UpdatePaymentMethod />} />
          <Route path="/mon-echeancier/validation" element={<ValidatePaymentMethod />} />
        </Routes>
      </ThemeProvider>
    </BrandContext.Provider>
  )
}

const App = () => {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
      <Router>
        <AppContent />
      </Router>
    </LocalizationProvider>

  )
}

export default App
